import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import cx from 'classnames';
import { X } from 'lucide-react';

export type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children:
    | React.ReactNode
    | {
        title: React.ReactNode;
        content: React.ReactNode;
      };
  /**
   * Actions pinned below the scrolling body, so they stay on screen however
   * long the content grows.
   */
  footer?: React.ReactNode;
  className?: string;
  sectionId?: string | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  longContent?: boolean;
  fitContent?: boolean;
  closeOnOutsideClick?: boolean;
  closeLabel?: string;
  'aria-labelledby'?: string;
};

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  footer,
  className,
  sectionId,
  size = 'md',
  fitContent = false,
  longContent = false,
  closeOnOutsideClick = true,
  closeLabel = 'Close modal',
  'aria-labelledby': ariaLabelledBy,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Outside click handler
  useEffect(() => {
    if (!closeOnOutsideClick) return;

    let hasOpened = false;
    const handleClickOutside = (event: MouseEvent) => {
      if (!hasOpened) {
        hasOpened = true;
        return;
      }
      const target = event.target as HTMLElement;
      if (target.closest('.modal-content')) return;
      onClose();
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, onClose, closeOnOutsideClick]);

  // Section scroll
  useEffect(() => {
    if (isOpen && sectionId) {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isOpen, sectionId]);

  // Save + restore focus; focus first focusable element on open
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      const focusFirst = () => {
        const focusable = containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
        focusable?.[0]?.focus();
      };
      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(focusFirst);
      } else {
        focusFirst();
      }
    } else {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  // Escape closes; Tab traps focus within modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusable = Array.from(
        containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const hasTitle =
    children && typeof children === 'object' && 'title' in children;
  const hasContent =
    children && typeof children === 'object' && 'content' in children;

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-900/50 dark:bg-black/60 z-40 transition-opacity"
        onClick={closeOnOutsideClick ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        className={cx(
          'modal-content fixed z-50',
          // Mobile: full screen
          'inset-0 w-full h-full max-md:flex max-md:flex-col',
          // Desktop: centered, sized by content. Releasing bottom/right lets
          // the box take its content height; with inset-0 still pinning it,
          // the -50% shift used half the viewport instead and pushed tall
          // dialogs off the bottom.
          'md:top-1/2 md:left-1/2 md:bottom-auto md:right-auto md:-translate-x-1/2 md:-translate-y-1/2',
          'md:w-auto md:max-w-[90vw] md:h-auto',
          {
            'md:max-w-max': fitContent,
            'md:w-96': !fitContent && size === 'sm',
            'md:w-1/2': !fitContent && size === 'md',
            'md:w-2/3': !fitContent && size === 'lg',
            'md:w-4/5': !fitContent && size === 'xl',
          },
          className
        )}
      >
        {/* Modal Content */}
        <div
          className={cx(
            'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl flex flex-col overflow-hidden',
            // Mobile: full height, no rounded corners
            'h-full rounded-none',
            // Desktop: content-sized with max-h cap, rounded corners
            'md:h-auto md:rounded-xl',
            {
              'md:min-h-[240px] md:max-h-[70vh]': size === 'sm' && !longContent,
              'md:min-h-[300px] md:max-h-[80vh]': size === 'md' && !longContent,
              'md:min-h-[300px] md:max-h-[85vh]': size === 'lg' && !longContent,
              'md:min-h-[300px] md:max-h-[90vh]': size === 'xl' && !longContent,
              'md:max-h-[90vh]': longContent,
            }
          )}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 p-2 rounded-lg text-muted-foreground hover:text-foreground-secondary hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors z-10"
            onClick={onClose}
            aria-label={closeLabel}
          >
            <X className="w-5 h-5" />
          </button>

          {hasTitle && hasContent ? (
            <>
              <div className="shrink-0 dark:bg-gray-800">{children.title}</div>
              <div className="min-h-0 flex-auto overflow-y-auto p-4 dark:bg-slate-800">
                {children.content}
              </div>
            </>
          ) : (
            // The body scrolls inside the height cap; without this, content
            // taller than the viewport was clipped along with its buttons.
            <div className="flex min-h-0 flex-auto flex-col overflow-y-auto">
              {children}
            </div>
          )}

          {footer && (
            <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
              {footer}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};
